// React core imports for hooks and components
import React, { useCallback, useContext, useEffect, useState } from "react";
// React Router for navigation and redirects
import { useNavigate, Navigate } from "react-router-dom";
// Material-UI components for UI elements
import { Button, Container, Box, Typography, CircularProgress, Alert } from "@mui/material";
// Material-UI styling system
import { makeStyles } from "@mui/styles";
// Breadcrumb context for navigation breadcrumbs
import { BreadcrumbContext } from "../../../components/Breadcrumb";
// Firebase services configuration
import {
  FirebaseAuth,
  Firestore,
  CloudFunctions,
} from "../../../components/FirebaseAuth/firebase";
// Firestore database operations
import { collection, getDocs, doc, updateDoc, query, orderBy, where, onSnapshot } from "firebase/firestore";
// Firebase cloud functions
import { httpsCallable } from "firebase/functions";
// Confirmation dialog for destructive actions
import { confirmAlert } from "react-confirm-alert";
// Toast notifications for user feedback
import { toast } from "react-toastify";
// Organization list item component
import { OrganizationItem } from "../../../components/Organizations/OrganizationItem";
// AI summary generator for admin users
import AdminSummaryGenerator from "../../../components/AIAssistant/AdminSummaryGenerator";
// Admin profile management component
import AdminProfileManager from "../../../components/Organizations/AdminProfileManager";
// Local storage utilities for user data
import {
  LOCAL_STORAGE_CURRENT_USER,
  getLocalStorage,
} from "../../../utils/localStorage";
// Utility to check if user has KWALL admin privileges
import { isKWALLAdmin } from "../../../utils";
import { useBatchSiteCounts } from "../../../hooks/useSiteCount";

// Material-UI styling configuration
const useStyles = makeStyles(() => ({
  container: {
    width: "100%", // Full width container
  },
  btnContainer: {
    display: "flex", // Flexbox layout
    flexDirection: "row", // Horizontal button arrangement
    justifyContent: "right", // Right-align buttons
    padding: 12, // Consistent padding
  },
}));

// Main component for displaying and managing organizations list (KWALL Admin only)
const OrganizationList = () => {
  // React Router navigation hook
  const history = useNavigate();
  // Material-UI styles hook
  const classes = useStyles();
  // Breadcrumb context to set page navigation
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  
  // State management for component data and UI state
  const [organizations, setOrganizations] = useState([]); // List of organizations
  const [isLoading, setIsLoading] = useState(false); // Loading state for initial data fetch
  const [isRefreshing, setIsRefreshing] = useState(false); // Loading state for CMS refresh job
  const [refreshResult, setRefreshResult] = useState(null); // Result of CMS refresh operation
  const [jobId, setJobId] = useState(null); // ID of currently running background job
  const [jobProgress, setJobProgress] = useState(null); // Real-time progress of background job
  const [isPopulatingTraffic, setIsPopulatingTraffic] = useState(false); // Loading state for traffic data population
  const [trafficPopulationResult, setTrafficPopulationResult] = useState(null); // Result of traffic population
  
  // Get current user from local storage to check admin permissions
  const currentUser = getLocalStorage(LOCAL_STORAGE_CURRENT_USER);

  // Batch fetch site counts for all organizations
  const { siteCounts, loading: loadingSiteCounts } = useBatchSiteCounts(
    organizations.map(org => org.id)
  );

  // Navigation handler to redirect to organization creation page
  const handleAdd = useCallback(() => {
    history("/organization_list/create");
  }, [history]);


  // Fetch organizations from Firestore using Firebase v9 modular syntax
  const getOrganizationList = useCallback(async () => {
    setIsLoading(true);
    let temp = [];
    
    // Create a query to get organizations ordered alphabetically by name
    const organizationsRef = collection(Firestore, "organizations");
    const q = query(organizationsRef, orderBy("name", "asc"));
    const organizationSnapShots = await getDocs(q);
  
    // Process each organization document, excluding deleted ones
    organizationSnapShots.forEach((doc) => {
      if (!doc.data().status || doc.data().status !== "deleted") {
        temp.push({ ...doc.data(), id: doc.id });
      }
    });
    setOrganizations(temp);
    setIsLoading(false);
  }, []);

  // Soft delete organization by setting status to "deleted" using Firebase v9 syntax
  const handleDeleteOrganization = useCallback(
    async (id) => {
      try {
        // Update organization document status to "deleted" (soft delete)
        const organizationRef = doc(Firestore, "organizations", id);
        await updateDoc(organizationRef, { status: "deleted" });
        // Refresh the organizations list to reflect changes
        getOrganizationList();
        toast.success("Deleted Successfully");
      } catch (ex) {
        toast.error("Something went wrong");
      }
    },
    [getOrganizationList]
  );

  // Show confirmation dialog before deleting organization
  const handleDelete = useCallback(
    (id) => {
      confirmAlert({
        title: "Are you sure?",
        message: "Are you sure you want to delete this? This cannot be undone.",
        buttons: [
          {
            label: "Yes",
            onClick: async () => handleDeleteOrganization(id), // Proceed with deletion
          },
          {
            label: "No",
            onClick: () => alert("Click No"), // Cancel deletion
          },
        ],
      });
    },
    [handleDeleteOrganization]
  );

  // Real-time monitoring of background CMS refresh job progress using Firestore listeners
  useEffect(() => {
    if (!jobId) return; // Exit if no job is running

    // Set up real-time listener for job document
    const jobRef = doc(Firestore, "jobs", jobId);
    const unsubscribe = onSnapshot(jobRef, (doc) => {
      if (doc.exists()) {
        const jobData = doc.data();
        setJobProgress(jobData); // Update progress in real-time
        
        // Handle job completion
        if (jobData.status === 'completed') {
          setIsRefreshing(false);
          setRefreshResult({
            success: true,
            message: 'CMS refresh job completed successfully!',
            ...jobData.progress // Include final progress statistics
          });
          toast.success("CMS data refresh completed successfully!");
          getOrganizationList(); // Refresh organizations list
          setJobId(null); // Clear job ID
        } 
        // Handle job failure
        else if (jobData.status === 'failed') {
          setIsRefreshing(false);
          setRefreshResult({
            error: true,
            message: `Job failed: ${jobData.error || 'Unknown error'}`
          });
          toast.error(`CMS refresh job failed: ${jobData.error || 'Unknown error'}`);
          setJobId(null); // Clear job ID
        }
      }
    });

    // Cleanup listener on component unmount or job change
    return () => unsubscribe();
  }, [jobId, getOrganizationList]);

  // Initiate CMS data refresh job for all organizations, accounts, and sites
  const handleRefreshAllCMSData = useCallback(async () => {
    confirmAlert({
      title: "Refresh All CMS Data",
      message: "This will process ALL organizations, accounts, and sites to ensure complete CMS/hosting data. The job will run in the background and you can monitor progress. Continue?",
      buttons: [
        {
          label: "Yes, Start Refresh Job",
          onClick: async () => {
            try {
              // Reset UI state for new job
              setIsRefreshing(true);
              setRefreshResult(null);
              setJobProgress(null);
              
              console.log("Starting CMS refresh job...");
              toast.info("Starting CMS refresh job... You can monitor progress below.");
              
              // Call Firebase Cloud Function to start background job
              const startCMSRefreshJob = httpsCallable(CloudFunctions, "kwallCloud-startCMSRefreshJob");
              const result = await startCMSRefreshJob();
              
              console.log("Job started:", result.data);
              setJobId(result.data.jobId); // Set job ID to enable progress monitoring
              
              toast.success("CMS refresh job started successfully!");
              
            } catch (error) {
              console.error("Failed to start job:", error);
              // Handle job start failure
              setRefreshResult({
                error: true,
                message: `Failed to start job: ${error.message}`
              });
              toast.error(`Failed to start refresh job: ${error.message}`);
              setIsRefreshing(false);
            }
          },
        },
        {
          label: "Cancel",
          onClick: () => {}, // No action on cancel
        },
      ],
    });
  }, []);

  // Test DataForSEO API directly
  const handleTestDataForSEOAPI = useCallback(async () => {
    try {
      console.log("🧪 Testing DataForSEO API...");
      toast.info("Testing DataForSEO API connection...");
      
      const testAPI = httpsCallable(CloudFunctions, "dataForSEOService-testDataForSEOAPI");
      const result = await testAPI();
      
      console.log("📊 DataForSEO API test result:", result.data);
      
      if (result.data.success) {
        toast.success("DataForSEO API test successful!");
        console.log("✅ API Results:", result.data.results);
      } else {
        toast.error(`DataForSEO API test failed: ${result.data.message}`);
        console.error("❌ API Error:", result.data.error);
      }
      
    } catch (error) {
      console.error("❌ Failed to test DataForSEO API:", error);
      toast.error(`Failed to test API: ${error.message}`);
    }
  }, []);

  // Populate traffic data for all sites in an organization (testing/backfill function)
  const handlePopulateOrganizationTrafficData = useCallback(async (organizationId, organizationName) => {
    confirmAlert({
      title: "Populate Traffic Data",
      message: `This will populate DataForSEO traffic data for ALL sites with URLs in "${organizationName}". This may take several minutes and will use API credits. Continue?`,
      buttons: [
        {
          label: "Yes, Populate Traffic Data",
          onClick: async () => {
            try {
              setIsPopulatingTraffic(true);
              setTrafficPopulationResult(null);
              
              console.log(`Starting traffic data population for organization ${organizationId}...`);
              toast.info("Starting traffic data population... This may take several minutes.");
              
              const populateTrafficData = httpsCallable(CloudFunctions, "dataForSEOService-populateOrganizationTrafficData");
              const result = await populateTrafficData({ organizationId });
              
              console.log("Traffic population result:", result.data);
              
              if (result.data.success) {
                setTrafficPopulationResult({
                  success: true,
                  message: result.data.message,
                  results: result.data.results
                });
                toast.success("Traffic data population completed!");
              } else {
                setTrafficPopulationResult({
                  error: true,
                  message: result.data.message
                });
                toast.error(`Traffic population failed: ${result.data.message}`);
              }
              
            } catch (error) {
              console.error("Failed to populate traffic data:", error);
              setTrafficPopulationResult({
                error: true,
                message: `Failed to populate traffic data: ${error.message}`
              });
              toast.error(`Failed to populate traffic data: ${error.message}`);
            } finally {
              setIsPopulatingTraffic(false);
            }
          },
        },
        {
          label: "Cancel",
          onClick: () => {},
        },
      ],
    });
  }, []);

  // Set breadcrumb navigation for the page
  useEffect(() => {
    setBreadcrumb([
      {
        to: "/",
        text: "Home",
        active: false, // Not the current page
      },
      {
        to: null,
        text: "Organizations",
        active: true, // Current page
      },
    ]);
  }, [setBreadcrumb]);

  // Load organizations list when component mounts
  useEffect(() => {
    getOrganizationList();
  }, [getOrganizationList]);

  // Security check: Only KWALL admins can access this page
  if (!isKWALLAdmin(currentUser?.role)) {
    return <Navigate to="/organization/switch-organization" />;
  }

  // Main component render
  return (
    <Container className={classes.container}>
      {/* Admin-only component for managing organization profiles */}
      <AdminProfileManager currentUser={currentUser} />
      
      {/* Admin-only AI assistant for generating organization summaries */}
      <AdminSummaryGenerator currentUser={currentUser} />
      
      {/* Action buttons container */}
      <Box className={classes.btnContainer}>
        {/* CMS refresh button - triggers background job to update all site data */}
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={handleRefreshAllCMSData}
          disabled={isRefreshing} // Disable while job is running
          style={{ marginRight: 16 }}
        >
          {isRefreshing ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              Refreshing CMS Data...
            </>
          ) : (
            "Refresh All CMS Data"
          )}
        </Button>
        {/* Test DataForSEO API button - Temporarily disabled until credentials are configured */}
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={() => toast.info("DataForSEO API credentials need to be configured in Firebase Functions")}
          style={{ marginRight: 8 }}
          disabled
        >
          <Box>Test DataForSEO API</Box>
        </Button>
        {/* Create new organization button */}
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={handleAdd}
        >
          <Box>Create New Organization</Box>
        </Button>
      </Box>
      
      {/* Traffic data population status display */}
      {isPopulatingTraffic && (
        <Box style={{ padding: 16, marginBottom: 16 }}>
          <Alert severity="info">
            <Typography variant="h6">Populating Traffic Data</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Fetching traffic data from DataForSEO for all sites with URLs... This may take several minutes.
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* Traffic population success result */}
      {trafficPopulationResult && !trafficPopulationResult.error && (
        <Box style={{ padding: 16, marginBottom: 16 }}>
          <Alert severity="success">
            <Typography variant="h6">Traffic Data Population Completed!</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {trafficPopulationResult.message}
            </Typography>
            {trafficPopulationResult.results && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Sites processed: {trafficPopulationResult.results.processed}/{trafficPopulationResult.results.totalSites} | 
                Failed: {trafficPopulationResult.results.failed}
                {trafficPopulationResult.results.errors && trafficPopulationResult.results.errors.length > 0 && (
                  <span style={{ color: 'orange' }}> | {trafficPopulationResult.results.errors.length} warnings</span>
                )}
              </Typography>
            )}
          </Alert>
        </Box>
      )}
      
      {/* Traffic population error result */}
      {trafficPopulationResult && trafficPopulationResult.error && (
        <Box style={{ padding: 16, marginBottom: 16 }}>
          <Alert severity="error">
            <Typography variant="h6">Traffic Data Population Failed</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {trafficPopulationResult.message}
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* Real-time job progress display - shows when job is running and progress data is available */}
      {isRefreshing && jobProgress && (
        <Box style={{ padding: 16, marginBottom: 16 }}>
          <Alert severity="info">
            <Typography variant="h6">CMS Refresh Job in Progress</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Status: {jobProgress.status} | Current Step: {jobProgress.currentStep}
            </Typography>
            {jobProgress.progress && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Progress: Organizations {jobProgress.progress.organizations?.processed || 0}/{jobProgress.progress.organizations?.total || 0} | 
                Accounts {jobProgress.progress.accounts?.processed || 0}/{jobProgress.progress.accounts?.total || 0} | 
                Sites {(jobProgress.progress.sites?.migrated || 0) + (jobProgress.progress.sites?.builtWithTriggered || 0)} processed
              </Typography>
            )}
          </Alert>
        </Box>
      )}
      
      {/* Fallback progress display - shows when job is starting but no progress data yet */}
      {isRefreshing && !jobProgress && (
        <Box style={{ padding: 16, marginBottom: 16 }}>
          <Alert severity="info">
            <Typography variant="h6">Starting CMS Refresh Job</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Initializing job... Progress will appear here shortly.
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* Success message display - shows job completion results and statistics */}
      {refreshResult && !refreshResult.error && (
        <Box style={{ padding: 16, marginBottom: 16 }}>
          <Alert severity="success">
            <Typography variant="h6">Refresh Completed Successfully!</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {refreshResult.message}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Organizations: {refreshResult.organizations?.processed}/{refreshResult.organizations?.total} | 
              Accounts: {refreshResult.accounts?.processed}/{refreshResult.accounts?.total} | 
              Sites: {refreshResult.sites?.migrated} migrated, {refreshResult.sites?.builtWithTriggered} analyzed
            </Typography>
            {refreshResult.errors && refreshResult.errors.length > 0 && (
              <Typography variant="body2" style={{ marginTop: 8, color: 'orange' }}>
                Warnings: {refreshResult.errors.length} issues encountered (check console for details)
              </Typography>
            )}
          </Alert>
        </Box>
      )}
      
      {/* Error message display - shows job failure information */}
      {refreshResult && refreshResult.error && (
        <Box style={{ padding: 16, marginBottom: 16 }}>
          <Alert severity="error">
            <Typography variant="h6">Refresh Failed</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {refreshResult.message}
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* Organizations list - render individual organization items when data is loaded */}
      {!isLoading &&
        organizations.length > 0 &&
        organizations.map((organization) => {
          return (
            <OrganizationItem
              organization={organization} // Pass organization data
              key={organization.id} // Unique key for React rendering
              handleDelete={() => handleDelete(organization.id)} // Delete handler
              siteCount={siteCounts[organization.id] || 0} // Pass site count from batch fetch
              onPopulateTrafficData={() => handlePopulateOrganizationTrafficData(organization.id, organization.name)} // Traffic data population handler
              isPopulatingTraffic={isPopulatingTraffic} // Loading state
            />
          );
        })}
    </Container>
  );
};

// Export the component as default export
export default OrganizationList;
